// frontend/src/context/ToastContext.jsx
import React, { createContext, useContext, useState, useCallback } from "react";

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = "info", duration = 5000) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type, duration }]);

    // Auto-remove toast after duration
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, duration);

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const clearToasts = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider
      value={{
        toasts,
        showToast,
        removeToast,
        clearToasts,
        success: (message, duration) => showToast(message, "success", duration),
        error: (message, duration) => showToast(message, "error", duration),
        warning: (message, duration) => showToast(message, "warning", duration),
        info: (message, duration) => showToast(message, "info", duration),
      }}
    >
      {children}
    </ToastContext.Provider>
  );
};

// ToastContainer Component
export const ToastContainer = () => {
  const { toasts, removeToast } = useToast();

  if (toasts.length === 0) return null;

  const getStyles = (type) => {
    const styles = {
      success: {
        background: "#dcfce7",
        color: "#16a34a",
        border: "1px solid #bbf7d0",
        icon: "✅",
      },
      error: {
        background: "#fee2e2",
        color: "#dc2626",
        border: "1px solid #fecaca",
        icon: "❌",
      },
      warning: {
        background: "#fef3c7",
        color: "#d97706",
        border: "1px solid #fde68a",
        icon: "⚠️",
      },
      info: {
        background: "#dbeafe",
        color: "#2563eb",
        border: "1px solid #bfdbfe",
        icon: "ℹ️",
      },
    };
    return styles[type] || styles.info;
  };

  return (
    <div
      style={{
        position: "fixed",
        top: "1rem",
        right: "1rem",
        zIndex: 10000,
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
        maxWidth: "400px",
        width: "100%",
      }}
    >
      {toasts.map((toast) => {
        const style = getStyles(toast.type);
        return (
          <div
            key={toast.id}
            style={{
              padding: "0.75rem 1rem",
              borderRadius: "8px",
              background: style.background,
              color: style.color,
              border: style.border,
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              animation: "slideIn 0.3s ease",
            }}
          >
            <span style={{ fontSize: "1.25rem" }}>{style.icon}</span>
            <span style={{ flex: 1, fontSize: "0.875rem" }}>
              {toast.message}
            </span>
            <button
              onClick={() => removeToast(toast.id)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "1rem",
                color: style.color,
                opacity: 0.6,
                padding: "0.25rem",
              }}
            >
              ✕
            </button>
          </div>
        );
      })}
    </div>
  );
};
